import { Component, Input, signal, inject, OnChanges, SimpleChanges, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AffaireService } from '../../../../core/services/affaire.service';
import { ProjectService } from '../../../../core/services/project.service';
import { Project, Affaire, PageResponse } from '../../../../core/models/models';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog.component';

@Component({
  selector: 'app-project-affaire',
  standalone: true,
  imports: [CommonModule, MatIconModule, ConfirmDialogComponent, RouterLink],
  templateUrl: './project-affaire.component.html',
  styleUrl: './project-affaire.component.scss'
})
export class ProjectAffaireComponent implements OnChanges {
  private affaireService = inject(AffaireService);
  private projectService = inject(ProjectService);

  @Input({ required: true }) project!: Project;
  @Input() readonlyMode = false;

  saved = output<Project>();
  error = output<string>();

  isSaving = signal(false);
  successMessage = signal('');
  localErrorMessage = signal('');

  // Affaire dropdown state
  affaireOptions = signal<Affaire[]>([]);
  affaireSearch = signal('');
  affairePage = signal(0);
  affaireTotalPages = signal(0);
  showAffaireDropdown = signal(false);
  isLoadingAffaires = signal(false);
  selectedAffaire = signal<Affaire | null>(null);

  // Confirmation dialog state
  showConfirmDelete = signal(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project'] && this.project) {
      if (this.project.affaireId) {
        this.loadSelectedAffaire(this.project.affaireId);
      } else {
        this.selectedAffaire.set(null);
      }
    }
  }

  loadSelectedAffaire(id: number): void {
    this.affaireService.getAffaireById(id).subscribe({
      next: (affaire) => this.selectedAffaire.set(affaire),
      error: () => this.selectedAffaire.set(null)
    });
  }

  toggleAffaireDropdown(): void {
    this.showAffaireDropdown.set(!this.showAffaireDropdown());
    if (this.showAffaireDropdown()) {
      this.resetAndLoadAffaires();
    }
  }

  onAffaireSearchChange(value: string): void {
    this.affaireSearch.set(value);
    this.resetAndLoadAffaires();
  }

  resetAndLoadAffaires(): void {
    this.affaireOptions.set([]);
    this.affairePage.set(0);
    this.affaireTotalPages.set(0);
    this.loadAffaires();
  }

  loadAffaires(): void {
    if (this.isLoadingAffaires()) return;
    this.isLoadingAffaires.set(true);
    this.affaireService.getAffaires(this.affaireSearch(), this.affairePage(), 10, 'numeroAffaire,asc')
      .subscribe({
        next: (resp: PageResponse<Affaire>) => {
          this.affaireOptions.set([...this.affaireOptions(), ...resp.content]);
          this.affaireTotalPages.set(resp.totalPages);
          this.isLoadingAffaires.set(false);
        },
        error: () => {
          this.isLoadingAffaires.set(false);
        }
      });
  }

  loadMoreAffaires(): void {
    if (this.affairePage() + 1 >= this.affaireTotalPages()) return;
    this.affairePage.set(this.affairePage() + 1);
    this.loadAffaires();
  }

  selectAffaire(affaire: Affaire): void {
    if (this.readonlyMode) {
      this.emitReadonlyMessage();
      return;
    }
    if (this.isProjectLocked()) {
      this.emitLockedMessage();
      return;
    }
    this.showAffaireDropdown.set(false);
    this.saveAffaireLink(affaire.id!, 'Affaire liee avec succes.', () => this.selectedAffaire.set(affaire));
  }

  detachAffaire(): void {
    if (this.readonlyMode) {
      this.emitReadonlyMessage();
      return;
    }
    if (this.isProjectLocked()) {
      this.emitLockedMessage();
      return;
    }
    this.showConfirmDelete.set(true);
  }

  confirmDetachAffaire(): void {
    if (this.isProjectLocked()) {
      this.emitLockedMessage();
      this.cancelDetachAffaire();
      return;
    }
    this.saveAffaireLink(null, 'Affaire detachee avec succes.', () => this.selectedAffaire.set(null));
    this.cancelDetachAffaire();
  }

  cancelDetachAffaire(): void {
    this.showConfirmDelete.set(false);
  }

  private saveAffaireLink(affaireId: number | null, successMessage: string, onSuccess: () => void): void {
    if (!this.project || !this.project.id) return;

    this.isSaving.set(true);
    const updatedProject: Project = { ...this.project, affaireId: affaireId };

    this.projectService.updateProject(this.project.id, updatedProject).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        this.localErrorMessage.set('');
        this.successMessage.set(successMessage);
        onSuccess();
        this.saved.emit(res);
        setTimeout(() => this.successMessage.set(''), 3000);
      },
      error: (err) => {
        this.isSaving.set(false);
        const message = err?.error?.message || err?.error || 'Erreur lors de la liaison de l\'affaire';
        this.localErrorMessage.set(message);
        this.error.emit(message);
      }
    });
  }

  displaySelectedAffaire(): string {
    const a = this.selectedAffaire();
    return a ? `${a.numeroAffaire} (${a.client.denomination})` : 'Sélectionner une affaire';
  }

  isProjectLocked(): boolean {
    return this.project?.metaStatus !== 'DRAFT';
  }

  private emitLockedMessage(): void {
    const message = "Le projet est verrouille (VALIDE/TERMINE). L'affaire ne peut plus etre modifiee.";
    this.localErrorMessage.set(message);
    this.error.emit(message);
  }

  private emitReadonlyMessage(): void {
    const message = "Vous n'avez pas les droits pour modifier l'affaire du projet.";
    this.localErrorMessage.set(message);
    this.error.emit(message);
  }
}
